import React, {
  createContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Children } from "types";
import { Alert, AlertTitle, Box, Snackbar, Typography } from "@mui/material";

export enum TOAST_TYPE {
  SUCCESS = "success",
  ERROR = "error",
  INFO = "info",
  WARNING = "warning",
}

export interface Toast {
  type: TOAST_TYPE;
  message: string;
  key?: number;
  action?: ReactNode;
}

const safeDecodeMessage = (message: string | undefined): string => {
  if (!message) {
    return '';
  }
  try {
    return decodeURIComponent(message);
  } catch {
    return message;
  }
};

export const ToastContext = createContext((toast: Toast) => {
  // do nothing - this is a placeholder function
});

const ToastContextProvider = ({ children }: Children): ReactNode => {
  const [open, setOpen] = useState<boolean>(false);
  const [activeAlert, setActiveAlert] = useState<Toast | null>();



  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setActiveAlert(null);
    }
  }, [activeAlert, open]);

  const displayToast = useCallback(
    (toast: Toast): void => {
      setActiveAlert(toast);
      setOpen(true);
    },
    [activeAlert],
  );

  return (
    <ToastContext.Provider value={displayToast}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          width: {
            xs: "100vw",
            lg: "50%",
          },
        }}
      >
        <Box sx={{ width: "100%" }}>
          {activeAlert ? (
            <Alert
              severity={activeAlert?.type}
              sx={{ width: "100%", mr: { xs: 2, lg: 0 } }}
            >
              <AlertTitle sx={{ textTransform: "capitalize" }}>
                <strong>{activeAlert?.type || ""}</strong>
              </AlertTitle>
              <Typography sx={{ textTransform: "lowercase" }}>
                {safeDecodeMessage(activeAlert?.message)}
              </Typography>
            </Alert>
          ) : (
            <></>
          )}
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export default ToastContextProvider;
